import { getAsTime, getAsLongDate, removeBrowserTimezoneConversion, formatAs } from '@commonClient/services/ukgDateTime';
import { QuotaType, EmployeeScheduleCalendarEventTypes } from '@enums/index';

const formatDateRange = (start, end) => `${getAsLongDate(start)} - ${getAsLongDate(end)}`;
const formatTimeRange = (start, end) => `${getAsTime(start)} - ${getAsTime(end)}`;

const formatSecondsToHourDisplay = (durationInTime) => {
    const hours = Math.floor(durationInTime / 3600).toString();
    const minutes = Math.floor((durationInTime % 3600) / 60).toString().padStart(2, '0');
    return `[${hours}:${minutes}]`;
};

const formatShiftTitle = (start, end) => {
    const duration = (new Date(end) - new Date(start)) / 1000;
    return `${formatTimeRange(start, end)} ${formatSecondsToHourDisplay(duration)}`;
};

const formatPayCodeLabel = (label, durationInTime) => `${label || ''} ${formatSecondsToHourDisplay(durationInTime)}`;

/**
 * @param {Dto.ISchedule} param0
 */
const mapShiftsAndPaycode = ({
    shifts, ukgPayCodes, awardedPayCodes, quotas, holidays, useShiftEndDate = false,
}) => {
    const mappedHolidays = holidays.map(({
        id, startDateTime, displayName,
    }) => ({
        id,
        start: new Date(startDateTime).toISOString(),
        end: new Date(startDateTime).toISOString(),
        rawStartString: startDateTime,
        rawEndString: startDateTime,
        type: EmployeeScheduleCalendarEventTypes.HOLIDAY,
        title: displayName,
        orgJobRefQualifier: undefined,
        segments: undefined,
    }));

    const mappedShifts = shifts.map(({
        id, startDateTime, endDateTime, label, segments,
    }) => {
        // Use end date for calendar positioning when useShiftEndDate is enabled
        // Extract date directly from the raw datetime string to avoid timezone shifting
        const rawCalendarDate = useShiftEndDate ? endDateTime : startDateTime;

        // Parse the date portion directly from the ISO string (YYYY-MM-DD)
        const dateOnly = rawCalendarDate.split('T')[0]; // Gets "2025-09-01" from "2025-09-01T15:00:00Z"
        const calendarDate = new Date(dateOnly + 'T00:00:00'); // Create date at start of day in local timezone
 
        return {
            id,
            start: calendarDate.toISOString(),
            end: calendarDate.toISOString(),
            rawStartString: rawCalendarDate,
            rawEndString: rawCalendarDate,
            type: EmployeeScheduleCalendarEventTypes.SHIFT,
            label,
            title: formatShiftTitle(startDateTime, endDateTime),
            dateString: formatDateRange(startDateTime, endDateTime),
            orgJobRefQualifier: segments?.[0]?.orgJobRef?.qualifier || label,
            segments: segments !== null ? segments.map((segment) => ({
                id: segment.id,
                label: segment.label,
                title: (segment.startTime && segment.endTime)
                    ? formatTimeRange(segment.startTime, segment.endTime)
                    : segment.type,
                type: segment.type,
                typeId: segment.typeId,
                startTime: segment.startTime,
                endTime: segment.endTime,
                transferStrings: segment.transferString?.split(';') ?? [],
            })) : null,
        };
    });

    const mappedUkgPaycodes = ukgPayCodes.map(({
        startDate, payCodeRef: { id, qualifier }, durationInTime,
    }) => ({
        id: id?.toString(),
        start: new Date(startDate).toISOString(),
        end: new Date(startDate).toISOString(),
        rawStartString: startDate,
        rawEndString: startDate,
        type: EmployeeScheduleCalendarEventTypes.UKG_PAYCODE,
        title: formatPayCodeLabel(qualifier, durationInTime),
        orgJobRefQualifier: undefined,
        segments: undefined,
    }));

    const mappedAwardedPayCodes = awardedPayCodes.map(({
        startDate, payCodeRef: { id, qualifier }, durationInTime,
    }) => ({
        id: id?.toString(),
        start: new Date(startDate).toISOString(),
        end: new Date(startDate).toISOString(),
        rawStartString: startDate,
        rawEndString: startDate,
        type: EmployeeScheduleCalendarEventTypes.AWARDED_PAYCODE,
        title: formatPayCodeLabel(qualifier, durationInTime),
        orgJobRefQualifier: undefined,
        segments: undefined,
    }));

    const getInclusiveEndDate = (endDate) => {
        const end = removeBrowserTimezoneConversion(endDate);
        end.setDate(end.getDate() + 1);
        return end.toISOString();
    };

    const mappedDailyQuotas = quotas
        .filter(({ quotaType }) => quotaType === QuotaType.Daily)
        .map(({
            startDate, endDate, amount, predictionCategory,
        }) => ({
            id: `${startDate.toString()}-${endDate.toString()}`,
            start: removeBrowserTimezoneConversion(startDate).toISOString(),
            end: getInclusiveEndDate(endDate),
            rawStartString: startDate,
            rawEndString: endDate,
            type: EmployeeScheduleCalendarEventTypes.DAILY_QUOTA,
            title: amount?.toString(),
            orgJobRefQualifier: undefined,
            segments: undefined,
            predictionCategory,
        }));

    const mappedWeeklyQuotas = quotas
        .filter(({ quotaType }) => quotaType === QuotaType.Weekly)
        .map(({
            startDate, endDate, amount, predictionCategory,
        }) => ({
            id: `${startDate.toString()}-${endDate.toString()}`,
            start: removeBrowserTimezoneConversion(startDate).toISOString(),
            end: getInclusiveEndDate(endDate),
            rawStartString: startDate,
            rawEndString: endDate,
            type: EmployeeScheduleCalendarEventTypes.WEEKLY_QUOTA,
            title: amount?.toString(),
            orgJobRefQualifier: undefined,
            segments: undefined,
            predictionCategory,
        }));
        
    /** @type {VBD.IScheduleItemWithDate[]} */
    return [
        ...mappedDailyQuotas,
        ...mappedHolidays,
        ...mappedShifts,
        ...mappedUkgPaycodes,
        ...mappedAwardedPayCodes,
        ...mappedWeeklyQuotas];
};

export default mapShiftsAndPaycode;
